import { memo, useEffect, useState, useCallback } from "react";
import Form from "../Form.js";
import {
    getOwners,
    getKentroOrganizations,
    getOwnersByOrganization,
} from "../../api/index.js";
import useSelectStyles from "../styles/Select.js";
import { Box, Typography } from "@mui/material";
import Spinner from "../Spinner.js";
import { jwt, useSnackbar } from "../../utils/index.js";

const KENTRO_OWNER_SELECT_SCREEN_CASES = [
    "AnimalRegistration",
    "AnimalRegistrationKentro",
    "MaleFamilyTree",
    "AnimalRelatives",
    "OwnerPopulation",
    "OwnerPopulationDetailed",
];

const SelectOwnerForm = ({
    setSelectedOwner,
    title,
    method,
    screenCase,
}) => {
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [formContent, setFormContent] = useState([]);

    // KentroUser-specific state: organizations under the kentro and owners per selected org
    const [kentroOrganizations, setKentroOrganizations] = useState([]);
    const [kentroOrgsDropdownValues, setKentroOrgsDropdownValues] = useState([]);
    const [kentroOwnersDropdownValues, setKentroOwnersDropdownValues] = useState([]);
    const [selectedKentroOrg, setSelectedKentroOrg] = useState("");
    const [isKentroUser, setIsKentroUser] = useState(null);
    const [months] = useState([
        { value: "Ιανουάριος", text: "Ιανουάριος", index: "1" },
        { value: "Φεβρουάριος", text: "Φεβρουάριος", index: "2" },
        { value: "Μάρτιος", text: "Μάρτιος", index: "3" },
        { value: "Απρίλιος", text: "Απρίλιος", index: "4" },
        { value: "Μάιος", text: "Μάιος", index: "5" },
        { value: "Ιούνιος", text: "Ιούνιος", index: "6" },
        { value: "Ιούλιος", text: "Ιούλιος", index: "7" },
        { value: "Αύγουστος", text: "Αύγουστος", index: "8" },
        { value: "Σεπτέμβριος", text: "Σεπτέμβριος", index: "9" },
        { value: "Οκτώβριος", text: "Οκτώβριος", index: "10" },
        { value: "Νοέμβριος", text: "Νοέμβριος", index: "11" },
        { value: "Δεκέμβριος", text: "Δεκέμβριος", index: "12" },
    ]);
    const [dropdownDates, setDrpopdownDates] = useState([]);

    const classes = useSelectStyles();
    const { error } = useSnackbar();
    const shouldUseKentroOwnerSelect =
        screenCase === "AnimalRegistrationKentro"
        || (Boolean(isKentroUser) && KENTRO_OWNER_SELECT_SCREEN_CASES.includes(screenCase));

    useEffect(() => {
        jwt.isKentroUser()
            .then(setIsKentroUser)
            .catch(() => setIsKentroUser(false));
    }, []);

    // Fetch owners when the KentroUser selects an organization
    const fetchOwnersByOrg = useCallback(async (orgId) => {
        if (!orgId) return;
        setIsLoading(true);
        try {
            setFetchedOwners([]);
            setKentroOwnersDropdownValues([]);
            const { owners } = await getOwnersByOrganization(orgId);
            setFetchedOwners(owners || []);
            setKentroOwnersDropdownValues(
                (owners || []).map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
                }))
            );
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    }, [error]);

    const getOwnerDropdownFields = useCallback(() => {
        if (!shouldUseKentroOwnerSelect) {
            return [
                {
                    customType: "dropdown",
                    id: "owner",
                    items: ownersDropdownValues,
                    placeholder: "Εκτροφή",
                    required: true,
                },
            ];
        }

        return [
            {
                customType: "dropdown",
                id: "kentroOrg",
                items: kentroOrgsDropdownValues,
                placeholder: "Συνεταιρισμός",
                defaultValue: selectedKentroOrg,
                required: true,
                onChange: async (event) => {
                    const orgName = event.target.value;
                    setSelectedKentroOrg(orgName);
                    const orgId = kentroOrganizations.find((org) => org.name === orgName)?._id;
                    await fetchOwnersByOrg(orgId);
                },
            },
            {
                customType: "dropdown",
                id: "owner",
                items: kentroOwnersDropdownValues,
                placeholder: "Εκτροφή",
                required: true,
                disabled: kentroOwnersDropdownValues.length === 0,
            },
        ];
    }, [
        shouldUseKentroOwnerSelect,
        ownersDropdownValues,
        kentroOrgsDropdownValues,
        selectedKentroOrg,
        kentroOrganizations,
        kentroOwnersDropdownValues,
        fetchOwnersByOrg,
    ]);

    const fetchData = useCallback(async () => {
        if (isKentroUser === null && KENTRO_OWNER_SELECT_SCREEN_CASES.includes(screenCase)) {
            return;
        }
        setIsLoading(true);
        try {
            if (shouldUseKentroOwnerSelect) {
                // KentroUser: fetch organizations under their kentro
                const { organizations } = await getKentroOrganizations();
                setKentroOrganizations(organizations || []);
                setKentroOrgsDropdownValues(
                    (organizations || []).map((org) => ({ value: org.name, text: org.name }))
                );
                setKentroOwnersDropdownValues([]);
                setFetchedOwners([]);
                setSelectedKentroOrg("");
            } else {
                const { owners } = await getOwners();
                setFetchedOwners(owners);
                setOwnersDropdownValues(owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
                })));
            }
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    }, [error, isKentroUser, screenCase, shouldUseKentroOwnerSelect]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (isKentroUser === null && KENTRO_OWNER_SELECT_SCREEN_CASES.includes(screenCase)) {
            setFormContent([]);
            return;
        }

        const currentYear = new Date().getFullYear();
        const years = Array.from({ length: currentYear - 1999 }, (_, i) => {
            const year = currentYear - i;
            return { value: year.toString(), text: year.toString() };
        });

        const today = new Date();
        const augustFirst = new Date(currentYear, 7, 1);
        const startYear = today >= augustFirst ? currentYear : currentYear - 1;
        const yearsWithMonths = Array.from({ length: startYear - 1999 + 1 }, (_, i) => startYear - i);

        const dates = yearsWithMonths.map((year) => ({
            value: `Αύγουστος ${year} - Ιούλιος ${year + 1}`,
            text: `Αύγουστος ${year} - Ιούλιος ${year + 1}`,
            startDateValue: year,
            endDateValue: year + 1,
        }));

        setDrpopdownDates(dates);

        switch (screenCase) {
            case "MaleFamilyTree":
                setFormContent([
                    ...getOwnerDropdownFields(),
                    {
                        customType: "button",
                        id: "submit",
                        type: "submit",
                        text: "Υποβολή",
                    },
                ]);
                break;
            case "AnimalRelatives":
                setFormContent([
                    ...getOwnerDropdownFields(),
                    {
                        customType: "dropdown",
                        id: "searchAnimals",
                        items: [
                            { value: "Λίστα Ζωντανών Ζώων", text: "Λίστα Ζωντανών Ζώων" },
                            { value: "Λίστα Νεκρών Ζώων", text: "Λίστα Νεκρών Ζώων" },
                        ],
                        placeholder: "Επιλογή Κατηγορίας",
                        defaultValue: "Λίστα Ζωντανών Ζώων",
                        required: true,
                    },
                    {
                        customType: "button",
                        id: "submit",
                        type: "submit",
                        text: "Υποβολή",
                    },
                ]);
                break;
            case "YearMilking":
                setFormContent([
                    ...getOwnerDropdownFields(),
                    {
                        customType: "dropdown",
                        id: "searchAnimals",
                        items: [
                            { value: "Λίστα Ζωντανών Ζώων", text: "Λίστα Ζωντανών Ζώων" },
                            // { value: "Λίστα Ζυγουριών", text: "Λίστα Ζυγουριών" },
                            { value: "Λίστα Νεκρών Ζώων", text: "Λίστα Νεκρών Ζώων" },
                        ],
                        placeholder: "Επιλογή Κατηγορίας",
                        defaultValue: "Λίστα Ζωντανών Ζώων",
                        required: true,
                    },
                    {
                        customType: "button",
                        id: "submit",
                        type: "submit",
                        text: "Υποβολή",
                    },
                ]);
                break;
            case "MonthlyMilkingProduction":
                setFormContent([
                    ...getOwnerDropdownFields(),
                    {
                        customType: "dropdown",
                        id: "month",
                        items: months,
                        placeholder: "Επιλογή Μήνα",
                        defaultValue: "Ιανουάριος",
                        required: true,
                    },
                    {
                        customType: "dropdown",
                        id: "year",
                        items: years,
                        placeholder: "Επιλογή Έτους",
                        defaultValue: currentYear.toString(),
                        required: true,
                    },
                    {
                        customType: "button",
                        id: "submit",
                        type: "submit",
                        text: "Υποβολή",
                    },
                ]);
                break;
            case "AnimalRegistration":
            case "AnimalRegistrationKentro":
                setFormContent([
                    ...getOwnerDropdownFields(),
                    {
                        customType: "button",
                        id: "submit",
                        type: "submit",
                        text: "Υποβολή",
                    },
                ]);
                break;
            case "OwnerPopulation":
                setFormContent([
                    ...getOwnerDropdownFields(),
                    {
                        customType: "dropdown",
                        id: "year",
                        items: years,
                        placeholder: "Επιλογή Έτους",
                        defaultValue: currentYear.toString(),
                        required: true,
                    },
                    {
                        customType: "button",
                        id: "submit",
                        type: "submit",
                        text: "Υποβολή",
                    },
                ]);
                break;
            case "OwnerPopulationDetailed":
                setFormContent([
                    ...getOwnerDropdownFields(),
                    {
                        customType: "dropdown",
                        id: "year",
                        items: dates,
                        placeholder: "Επιλογή Περιόδου",
                        defaultValue: `Αύγουστος ${currentYear - 1} - Ιούλιος ${currentYear}`,
                        required: true,
                    },
                    {
                        customType: "button",
                        id: "submit",
                        type: "submit",
                        text: "Υποβολή",
                    },
                ]);
                break;
            default:
                setFormContent(null);
        }
    }, [screenCase, ownersDropdownValues, kentroOrgsDropdownValues, kentroOwnersDropdownValues, selectedKentroOrg, isKentroUser, getOwnerDropdownFields]);


    const handleOwnerSelect = async (values) => {
        const selOwner = fetchedOwners.find((owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner)
        setSelectedOwner(selOwner);
        if (screenCase === "MaleFamilyTree" || screenCase === "AnimalRegistration" || screenCase === "AnimalRegistrationKentro") {
            await method(selOwner?._id)
        }
        var status = ""
        if (screenCase === "AnimalRelatives") {
            if (values.searchAnimals === "Λίστα Ζωντανών Ζώων") {
                status = "alive"
            } else {
                status = "dead"
            }
            await method(selOwner?._id, status)
        }
        if (screenCase === "MonthlyMilkingProduction") {
            const month = months.find((m) => m.value === values.month)?.index
            await method(selOwner?._id, values.year, month, values.month)
        }
        if (screenCase === "OwnerPopulation") {
            await method(selOwner?._id, values?.year);
        }
        if (screenCase === "OwnerPopulationDetailed") {
            await method(selOwner?._id, dropdownDates.find((date) => date.value === values.year)?.startDateValue);
        }
        if (screenCase === "YearMilking") {
            if (values.searchAnimals === "Λίστα Ζωντανών Ζώων") {
                status = "alive"
            } else if (values.searchAnimals === "Λίστα Ζυγουριών") {
                status = "zyg"
            } else {
                status = "dead"
            }
            await method(selOwner?._id, status);
        }
    }
    return (
        <>
            <Spinner open={isLoading} />
            <Box className={classes.outerBox}>
                <Box container flexDirection="column" className={classes.innerBox}>
                    <Typography variant="h5" sx={{ margin: "1rem" }}>
                        {title}
                    </Typography>
                    <Form content={formContent} onSubmit={handleOwnerSelect} />
                </Box>
            </Box>
        </>
    )
}

export default memo(SelectOwnerForm);
